import { Subject } from 'rxjs';
import { OutsideZone } from '../../services/utils/zone/zone.decorators';
import { _LocalSpaceConfig, LocalSpaceStorage } from './localspace.storage';

export interface StorageConfig<Storage extends StorageInterface<any, any>> {
    name?: string;
    version?: number;
    size?: number;
    storeName?: string;
    description?: string;
    dbKey?: string;
    storage?: (config: any) => Storage;
}

const defaultConfig: StorageConfig<any> & any = {
    storage: (config: _LocalSpaceConfig) => new LocalSpaceStorage(config)
};

@OutsideZone()
export class InternalStorage<Storage extends StorageInterface<any, any> = any, Config extends StorageConfig<Storage> = any> {
    #config: Config;
    #databaseInstance: Promise<Storage>;

    #onChange: Subject<void> = new Subject();
    public get onChange() {
        return this.#onChange.asObservable();
    }

    /**
     * Create a new Storage instance using the order of drivers and any additional config
     * options to pass to LocalForage.
     *
     * Possible default driverOrder options are: ['indexeddb', 'localstorage'] and the
     * default is that exact ordering.
     */
    constructor(config: Config = defaultConfig) {
        this.#config = { ...defaultConfig, ...config };
        this.#databaseInstance = this.create();
    }

    private async create(): Promise<Storage> {
        return this.#config.storage!(this.#config);
    }

    /**
     * Get the value associated with the given key.
     *
     * @param key the key to identify this value
     * @returns Returns a promise with the value of the given key
     */
    async get<T = any>(key: string): Promise<T> {
        const db = await this.#databaseInstance;
        return db.get(key).then(res => convertDates<T>(res as any));
    }

    /**
     * Set the value for the given key.
     *
     * @param key the key to identify this value
     * @param value the value for this key
     * @returns Returns a promise that resolves when the key and value are set
     */
    async set(key: string, value: any): Promise<void> {
        const db = await this.#databaseInstance;
        return db.set(key, value).then(() => {
            this.#onChange.next();
            return;
        });
    }

    /**
     * Remove any value associated with this key.
     *
     * @param key the key to identify this value
     * @returns Returns a promise that resolves when the value is removed
     */
    async remove(key: string): Promise<void> {
        const db = await this.#databaseInstance;
        return db.remove(key).then((res) => {
            if (res)
                this.#onChange.next();

            return;
        });
    }

    /**
     * Clear the entire key value store. WARNING: HOT!
     *
     * @returns Returns a promise that resolves when the store is cleared
     */
    async clear(): Promise<any> {
        const db = await this.#databaseInstance;
        await db.clear();

        // const keys = await this.keys();
        // const p = keys.map(async key => db.delete(key));
        // await Promise.all(p);

        this.#onChange.next();
        return Promise.resolve();
    }

    /**
     * @returns Returns a promise that resolves with the number of keys stored.
     */
    async length(): Promise<number> {
        const db = await this.#databaseInstance;
        return db.length();
    }

    /**
     * @returns Returns a promise that resolves with the keys in the store.
     */
    async keys(): Promise<string[]> {
        const db = await this.#databaseInstance;
        return db.keys();
    }

    /**
     * Iterate through each key,value pair.
     *
     * @param iteratorCallback a callback of the form (value, key, iterationNumber)
     * @returns Returns a promise that resolves when a value is returned or the iteration has finished.
     */
    forEach<T = any>(iteratorCallback: (value: T, key: string, iterationNumber: number) => T): Promise<T | undefined>;

    /**
     * Iterate through each key,value pair.
     *
     * @param iteratorCallback a callback of the form (value, key, iterationNumber)
     * @returns Returns a promise that resolves when the iteration has finished.
     */
    forEach<T = any>(iteratorCallback: (value: T, key: string, iterationNumber: number) => void): Promise<undefined>;

    /**
     * Iterate through each key,value pair.
     *
     * @param iteratorCallback a callback of the form (value, key, iterationNumber)
     * @returns Returns a promise that resolves when a value is returned or the iteration has finished.
     */
    async forEach<T = any>(iteratorCallback: (value: T, key: string, iterationNumber: number) => any): Promise<undefined | T> {
        const db = await this.#databaseInstance;
        return db.forEach(iteratorCallback);
    }
}

export interface StorageInterface<Storage, Config> {
    get config(): Config;
    create(): Promise<Storage>;
    get<T>(key: string): Promise<T | undefined>;
    set<T>(key: string, value: T): Promise<void>;
    remove(key: string): Promise<boolean>;
    clear(): Promise<void>;
    length(): Promise<number>;
    keys(): Promise<string[]>;
    forEach<T>(iteratorCallback: (value: T, key: string, iterationNumber: number) => any): Promise<undefined | T>;
}

const dateRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)$/;
const utcDateRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)Z$/;

export const convertDates = <T>(object: T): T => {
    if (!object || !(object instanceof Object))
        return object;

    if (object instanceof Array)
        for (const item of object)
            convertDates(item);

    for (const key of Object.keys(object)) {
        const value = (object as any)[key];

        if (value instanceof Array)
            for (const item of value)
                convertDates(item);

        if (value instanceof Object)
            convertDates(value);

        if (typeof value === 'string' && utcDateRegex.test(value))
            (object as any)[key] = new Date(value);

        if (typeof value === 'string' && dateRegex.test(value))
            (object as any)[key] = new Date(value);
    }

    return object;
};

export const cacheClean = () => {

    return new Promise((resolve, reject) => {
        const req = indexedDB.deleteDatabase("B2Cap");
        req.onsuccess = resolve;
        req.onerror = reject;
      });

}
